import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { SocketContext } from '../../socket';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import {
    DUPLICATE_CONFLICT_TEXT, applyDuplicateConflicts, blankReviewRow, duplicateConflictsOf, reviewRowError, toConfirmRow, toReviewRow,
} from './missedCallFormat';

const POLL_MS = 3000;
const WAITING = ['PENDING', 'PROCESSING'];

/**
 * The screenshot import without its screen (docs/MISSED_CALLS_PLAN.md §3.1–3.2, §6.5):
 *   confirm → pick → upload → reading → review → 저장, with FAILED offering 다시 시도.
 * The desktop dialog and the phone page share it, so the rules live in one place. The phone page runs
 * outside the admin layout and has no socket there; the 3-second poll carries the wait on its own.
 */
export const useScreenshotImport = ({ config, onGone }) => {
    const socket = useContext(SocketContext);
    const { enqueueSnackbar } = useSnackbar();
    const [step, setStep] = useState('confirm'); // confirm | pick | upload | reading | review | failed
    const [files, setFiles] = useState([]);      // [{ file, url }]
    const [progress, setProgress] = useState(0);
    const [imp, setImp] = useState(null);
    const [review, setReview] = useState([]);
    const [busy, setBusy] = useState(false);
    // The days the uploader ticked (plan §3.9). One screenshot run can show several; rows on any other day are
    // dropped. Empty means today, which is what it starts as.
    const [listDates, setListDates] = useState([]);
    const maxFiles = config?.max_screenshots || 10;
    const maxMb = config?.max_screenshot_mb || 10;
    const importId = imp?.id;

    // Previews are revoked when their file goes and when the page goes — never while still on screen.
    const filesRef = useRef(files);
    filesRef.current = files;
    useEffect(() => () => filesRef.current.forEach((f) => URL.revokeObjectURL(f.url)), []);

    const onGoneRef = useRef(onGone);
    onGoneRef.current = onGone;

    const reset = useCallback(() => {
        filesRef.current.forEach((f) => URL.revokeObjectURL(f.url));
        setStep('confirm');
        setListDates([]);   // back to today only
        setFiles([]);
        setImp(null);
        setReview([]);
        setProgress(0);
    }, []);

    /** Adds what was picked, up to max_screenshots, and says what it left out — `rejected` is what the picker itself dropped. */
    const addFiles = useCallback((accepted, rejected = 0) => {
        const taken = accepted.slice(0, Math.max(0, maxFiles - filesRef.current.length));
        if (rejected) enqueueSnackbar(`${rejected}개 파일은 이미지가 아니거나 ${maxMb}MB를 넘어 뺐습니다`, { variant: 'warning' });
        if (accepted.length > taken.length) {
            enqueueSnackbar(`한 번에 ${maxFiles}장까지만 올릴 수 있어 ${accepted.length - taken.length}장은 빼고 넣었습니다`, { variant: 'warning' });
        }
        if (taken.length) setFiles((prev) => [...prev, ...taken.map((file) => ({ file, url: URL.createObjectURL(file) }))].slice(0, maxFiles));
    }, [enqueueSnackbar, maxFiles, maxMb]);

    const removeFile = useCallback((url) => {
        URL.revokeObjectURL(url);
        setFiles((prev) => prev.filter((f) => f.url !== url));
    }, []);

    const refreshImport = useCallback(async (id) => {
        const { import: fresh } = await missedCallsApi.getImport(id);
        setImp(fresh);
        if (fresh.status === 'READY') {
            setReview(fresh.candidates.map(toReviewRow));
            setStep('review');
        } else if (fresh.status === 'FAILED') {
            setStep('failed');
        } else if (!WAITING.includes(fresh.status) && onGoneRef.current) {
            onGoneRef.current(); // saved or canceled elsewhere
        }
    }, []);

    // Wait for the reading: the socket says when it moved on; the poll covers a dropped or absent socket.
    useEffect(() => {
        if (step !== 'reading' || !importId) return undefined;
        const onStatus = (e) => {
            if (e?.import_id === importId && !WAITING.includes(e.status)) refreshImport(importId).catch(() => {});
        };
        if (socket) socket.on('missedCalls.import', onStatus);
        const poll = setInterval(() => refreshImport(importId).catch(() => {}), POLL_MS);
        return () => {
            if (socket) socket.off('missedCalls.import', onStatus);
            clearInterval(poll);
        };
    }, [step, importId, socket, refreshImport]);

    const upload = useCallback(async () => {
        setStep('upload');
        setProgress(0);
        try {
            const { import: created } = await missedCallsApi.upload(filesRef.current.map((f) => f.file), setProgress, listDates);
            setImp(created);
            setStep('reading');
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
            setStep('pick');
        }
    }, [enqueueSnackbar, listDates]);

    /** Cancels an import that is still in flight — its screenshots are deleted. */
    const discard = useCallback(async () => {
        if (importId && ['reading', 'review', 'failed'].includes(step)) {
            try { await missedCallsApi.discardImport(importId); } catch { /* the hourly cleanup discards it anyway */ }
        }
    }, [importId, step]);

    const retry = useCallback(async () => {
        setBusy(true);
        try {
            const { import: again } = await missedCallsApi.retryImport(importId);
            setImp(again);
            setStep('reading');
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        } finally {
            setBusy(false);
        }
    }, [importId, enqueueSnackbar]);

    /** Saves the ticked rows → { import_id, created, merged, rows }, or null when it failed. */
    const save = useCallback(async () => {
        setBusy(true);
        try {
            return await missedCallsApi.confirmImport(importId, review.map(toConfirmRow).filter(Boolean));
        } catch (err) {
            // Saved or canceled somewhere else meanwhile (the phone and the desktop, or a second 저장): say which and leave.
            const gone = err?.response?.status === 409 ? err.response.data?.import_status : null;
            if (gone === 'CONFIRMED' || gone === 'DISCARDED') {
                enqueueSnackbar(gone === 'CONFIRMED' ? '이 업로드는 이미 저장되었습니다' : '이 업로드는 취소되었습니다', { variant: 'info' });
                if (onGoneRef.current) onGoneRef.current({ notified: true });
                return null;
            }
            // Someone added one of these numbers while this was open: nothing was saved, ask again for those rows.
            const conflicts = duplicateConflictsOf(err);
            if (conflicts) {
                setReview((prev) => applyDuplicateConflicts(prev, conflicts));
                enqueueSnackbar(DUPLICATE_CONFLICT_TEXT, { variant: 'warning' });
            } else {
                enqueueSnackbar(errorMessage(err), { variant: 'error' });
            }
            return null;
        } finally {
            setBusy(false);
        }
    }, [importId, review, enqueueSnackbar]);

    const setRow = useCallback((key, patch) => setReview((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r))), []);
    // The days this import covers: what the server recorded once it exists, else what is ticked, else today.
    const importDays = (imp?.list_dates && imp.list_dates.length ? imp.list_dates : null)
        || (listDates.length ? listDates : null)
        || (config?.today ? [config.today] : []);
    // The primary (earliest) day — what the single-day copy on screen refers to.
    const importDate = imp?.list_date || importDays[0] || null;
    /**
     * Where a row typed in during the review starts. Deliberately NOT the earliest day: the row most likely to be
     * added by hand is one the screenshots missed *today*, and on a multi-day upload the earliest day is days back —
     * the row would be filed silently onto Sunday's list. Today when today is covered, else the most recent day
     * that is. Multi-day reviews also let the day be changed per row, so a wrong guess is visible and fixable.
     */
    const addDay = (config?.today && importDays.includes(config.today) ? config.today : null)
        || importDays[importDays.length - 1] || importDate;
    // A past day's rows show no clock on the iPhone, so their time may stay blank (시간 모름) — decided per ROW,
    // not per import: on a today + 어제 upload, a blank time on a *today* row is a mistake, and the server refuses
    // the whole save (in English) rather than invent one.
    const isPastDay = (d) => Boolean(config?.today && d && d < config.today);
    const multiDay = importDays.length > 1;
    const pastDay = importDays.some(isPastDay);
    const errors = review.map((r) => reviewRowError(r, {
        timeOptional: isPastDay(multiDay ? r.list_date : (r.list_date || importDate)),
        // Multi-day: every saved row has to name the list it joins. A 다른 날 candidate carries none and the server
        // refuses the save rather than guess — so ask here, while it can still be answered or unticked.
        dayRequired: multiDay,
    }));

    // A row typed in during the review needs a day too (see addDay).
    const addRow = useCallback(
        () => setReview((prev) => [...prev, blankReviewRow(`added-${Date.now()}`, addDay || null)]),
        [addDay],
    );
    return {
        step, setStep, files, addFiles, removeFile, reset, progress, imp, review, setRow, addRow, busy,
        upload, discard, retry, save, errors, listDates, setListDates, importDays, importDate, pastDay,
        multiDay, addDay,
        included: review.filter((r) => r.include).length,
        canSave: !busy && !errors.some(Boolean),
        maxFiles, maxMb,
    };
};

export default useScreenshotImport;
